import { cn } from "@/lib/utils";
import { validatePadsplitProfileUrl } from "@shared/padsplit-profile-url";

type Size = "compact" | "sm" | "md";

const SAGE_GREEN =
  "bg-green-50 text-green-800 dark:bg-green-950/40 dark:text-green-400";
const PADSPLIT_ORANGE =
  "bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300";

const SIZE_CLASSES: Record<Size, string> = {
  compact:
    "rounded px-1 py-px text-[9px] font-semibold uppercase leading-none tracking-wide",
  sm: "rounded-full px-1.5 py-0.5 text-[10px] font-medium",
  md: "rounded-md px-2 py-0.5 text-xs font-medium",
};

export interface MemberSourceBadgeProps {
  padsplitMemberId?: string | null;
  memberId: number | string;
  size?: Size;
  className?: string;
  /** When provided for a PadSplit member, renders the badge as a clickable
   *  link that opens the member's validated host occupant profile in a new tab.
   *  Click is stopped from propagating so a row-level onClick is not triggered. */
  href?: string | null;
}

export function MemberSourceBadge({
  padsplitMemberId,
  memberId,
  size = "md",
  className,
  href,
}: MemberSourceBadgeProps) {
  const isPadsplit = !!padsplitMemberId && String(padsplitMemberId).trim() !== "";
  const sizeClasses = SIZE_CLASSES[size];
  const profileHref = validatePadsplitProfileUrl(href);

  if (isPadsplit) {
    const label = size === "compact" ? "PS" : "PadSplit";
    const baseTitle = `PadSplit member${padsplitMemberId ? ` (#${padsplitMemberId})` : ""}`;

    if (profileHref) {
      return (
        <a
          href={profileHref}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "shrink-0 inline-flex items-center cursor-pointer underline-offset-2 hover:underline",
            sizeClasses,
            PADSPLIT_ORANGE,
            className,
          )}
          title="Open on PadSplit"
          data-testid={`badge-source-padsplit-${memberId}`}
          onClick={(e) => e.stopPropagation()}
        >
          {label}
        </a>
      );
    }

    return (
      <span
        className={cn("shrink-0 inline-flex items-center", sizeClasses, PADSPLIT_ORANGE, className)}
        title={`${baseTitle} — PadSplit profile unavailable`}
        data-testid={`badge-source-padsplit-${memberId}`}
      >
        {label}
      </span>
    );
  }

  return (
    <span
      className={cn("shrink-0 inline-flex items-center", sizeClasses, SAGE_GREEN, className)}
      title="Direct member (not synced from PadSplit)"
      data-testid={`badge-source-direct-${memberId}`}
    >
      {size === "compact" ? "D" : "Direct"}
    </span>
  );
}

export default MemberSourceBadge;
